/**
 * Centralized Currency Formatter for ETPOS SPA.
 * Formats values based on business settings saved in localStorage.
 * Falls back to default USD formatting if settings are not present.
 */

// Default settings
const DEFAULT_SYMBOL = '$';
const DEFAULT_PLACEMENT = 'before';
const DEFAULT_PRECISION = 2;
const DEFAULT_DECIMAL = '.';
const DEFAULT_THOUSAND = ',';

// Helper to get cached settings
const getSetting = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch (e) {
    return fallback;
  }
};

/**
 * Main function to format numeric amount to currency string.
 * Supports: dynamic currency symbol, placement (before/after), precision, thousand/decimal separators.
 * Example: 1234.56 -> "$ 1,234.56" or "1.234,56 €"
 */
export const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '';
  }

  const symbol = getSetting('etpos_currency_symbol', DEFAULT_SYMBOL);
  const placement = getSetting('etpos_currency_placement', DEFAULT_PLACEMENT);
  const precision = parseInt(getSetting('etpos_currency_precision', DEFAULT_PRECISION), 10);
  const decimalSep = getSetting('etpos_decimal_separator', DEFAULT_DECIMAL);
  const thousandSep = getSetting('etpos_thousand_separator', DEFAULT_THOUSAND);

  const num = parseFloat(amount);
  const fixedNum = num.toFixed(precision);
  const parts = fixedNum.split('.');

  // Add thousand separators
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);

  // Re-join with decimal separator
  const formattedNumber = parts.join(decimalSep);

  if (placement === 'before') {
    return `${symbol} ${formattedNumber}`;
  } else {
    return `${formattedNumber} ${symbol}`;
  }
};

/**
 * Formats a currency amount without the symbol.
 * Only applies precision and separators.
 */
export const formatCurrencyNumber = (amount) => {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '';
  }

  const precision = parseInt(getSetting('etpos_currency_precision', DEFAULT_PRECISION), 10);
  const decimalSep = getSetting('etpos_decimal_separator', DEFAULT_DECIMAL);
  const thousandSep = getSetting('etpos_thousand_separator', DEFAULT_THOUSAND);

  const num = parseFloat(amount);
  const fixedNum = num.toFixed(precision);
  const parts = fixedNum.split('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);

  return parts.join(decimalSep);
};

/**
 * Parses a formatted currency string back into a standard JavaScript number (float).
 */
export const parseCurrency = (formattedAmount) => {
  if (formattedAmount === undefined || formattedAmount === null) return 0;
  if (typeof formattedAmount === 'number') return formattedAmount;
  if (typeof formattedAmount !== 'string') return parseFloat(formattedAmount) || 0;

  const decimalSep = getSetting('etpos_decimal_separator', DEFAULT_DECIMAL);
  const thousandSep = getSetting('etpos_thousand_separator', DEFAULT_THOUSAND);

  // Remove any non-numeric/non-decimal characters except negative sign, decimal separator, and thousand separator
  // Keep: digits, minus sign, decimal separator, thousand separator
  const regexClean = new RegExp(`[^0-9\\-${thousandSep === '.' ? '\\.' : thousandSep}${decimalSep === '.' ? '\\.' : decimalSep}]`, 'g');
  let cleaned = formattedAmount.replace(regexClean, '');

  // Remove thousands separator
  if (thousandSep) {
    const regexThousand = new RegExp(thousandSep === '.' ? '\\.' : thousandSep, 'g');
    cleaned = cleaned.replace(regexThousand, '');
  }

  // Replace decimal separator with standard point (dot)
  if (decimalSep && decimalSep !== '.') {
    cleaned = cleaned.replace(decimalSep, '.');
  }

  return parseFloat(cleaned) || 0;
};

/**
 * Updates cached settings in localStorage from the response data.
 */
export const updateCurrencySettings = (data) => {
  if (!data) return;
  
  try {
    if (data.currency_symbol !== undefined) {
      localStorage.setItem('etpos_currency_symbol', data.currency_symbol);
    }
    if (data.currency_symbol_placement !== undefined) {
      localStorage.setItem('etpos_currency_placement', data.currency_symbol_placement);
    }
    if (data.currency_precision !== undefined) {
      localStorage.setItem('etpos_currency_precision', String(data.currency_precision));
    }
    if (data.decimal_separator !== undefined) {
      localStorage.setItem('etpos_decimal_separator', data.decimal_separator);
    }
    if (data.thousand_separator !== undefined) {
      localStorage.setItem('etpos_thousand_separator', data.thousand_separator);
    }
  } catch (e) {
    // console.error('Error saving currency settings to localStorage:', e);
  }
};

/**
 * Clears currency settings from localStorage on logout.
 */
export const clearCurrencySettings = () => {
  try {
    localStorage.removeItem('etpos_currency_symbol');
    localStorage.removeItem('etpos_currency_placement');
    localStorage.removeItem('etpos_currency_precision');
    localStorage.removeItem('etpos_decimal_separator');
    localStorage.removeItem('etpos_thousand_separator');
  } catch (e) {
    // console.error('Error clearing currency settings from localStorage:', e);
  }
};

export default {
  formatCurrency,
  formatCurrencyNumber,
  parseCurrency,
  updateCurrencySettings,
  clearCurrencySettings,
};
